import clsx from 'clsx'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff } from 'selectors/accountSelectors'
import { LOYALTY_SPEND_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { LOYALTY_CREDIT_CARD_COLOUR_CREAM, LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS } from './loyaltyCreditCardConstants'
import { LoyaltyCreditCardSection, loyaltyCreditCardWrappingLabel } from './components/loyaltyCreditCardStyles'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'

const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    gap: ${rem(24)};
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, minmax(0, 1fr));

    /* Employees don't see the welcome-credit tile, so the row closes up to
       three columns instead of leaving an empty fourth. */
    &.is-three-up {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
`

const FeatureCard = styled.div`
  display: grid;
  grid-template-columns: ${rem(110)} minmax(0, 1fr);
  column-gap: ${rem(16)};
  row-gap: ${rem(4)};
  align-items: start;
  padding-bottom: ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    gap: ${rem(12)};
    padding-bottom: 0;
    border-bottom: none;
  }
`

const FeatureCardImage = styled.div`
  width: ${rem(110)};
  border-radius: ${rem(8)};
  overflow: hidden;
  background: ${LOYALTY_CREDIT_CARD_COLOUR_CREAM};
  height: ${rem(99)};
  grid-row: 1 / span 4;

  ${mediaQueryUp.tablet} {
    width: 100%;
    height: auto;
    aspect-ratio: 267 / 331;
    grid-row: auto;
  }

  picture {
    display: block;
    height: 100%;
    width: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const FeatureCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)};
  }
`

const MobileFeatureCardContent = styled(FeatureCardContent)`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const TabletFeatureCardContent = styled(FeatureCardContent)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const MobileBonusText = styled(BodyText)`
  && {
    color: #009B79;
  }
`

const FeatureCardTextButton = styled(TextButton)`
  && {
    align-self: flex-start;
    font-weight: ${props => props.theme.font.secondary.weight.bold};
    justify-content: flex-start;
    justify-self: start;
    margin: 0;
    padding: 0;
    text-align: start;

    ${loyaltyCreditCardWrappingLabel}
  }
`

const HighlightCtaGroup = styled(Group)`
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: auto;
  }
`

const HighlightApplyButton = styled(LoyaltyCreditCardApplyNowButton)`
  && {
    min-width: ${rem(148)};

    ${loyaltyCreditCardWrappingLabel}
  }
`

function LoyaltyCreditCardHighlightsSection() {
  const intl = useIntl()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isStaff = useAppSelector(isLEStaff)

  return (
    <LoyaltyCreditCardSection background="normal-background" id="benefits">
      <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="large">
        <VerticalSpacer gap={40}>
          <VerticalSpacer gap={8}>
            <Heading variant="heading1" align="center">
              {intl.formatMessage(
                {
                  id: 'BCEa6aK7bePyCGQauw4-',
                  defaultMessage: 'It all points to your next {italic}',
                  description: 'Highlights section heading on Société Credit Card page',
                },
                {
                  italic: (
                    <em>
                      {intl.formatMessage({
                        id: 'tFHffhE5NeCS09qwugja',
                        defaultMessage: 'holiday',
                        description: 'Italic emphasis text in the highlights heading on the Société Credit Card page',
                      })}
                    </em>
                  ),
                },
              )}
            </Heading>
            <BodyText variant="medium" align="center">
              {intl.formatMessage({
                id: 'ynO_S8GTU8plomSbj7vT',
                defaultMessage: 'A smarter way to earn — and to travel.',
                description: 'Highlights section subtitle on Société Credit Card page',
              })}
            </BodyText>
          </VerticalSpacer>
          <CardGrid className={clsx({ 'is-three-up': isStaff })}>
            <FeatureCard>
              <FeatureCardImage>
                <ResponsiveImage
                  id="societe-tile-points-beach"
                  fit="center"
                  objectFit="cover"
                />
              </FeatureCardImage>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: 'tea0jP4hpScX-ar3N_F1',
                  defaultMessage: '2 Société Points per $1',
                  description: 'Highlight card 1 heading on Société Credit Card page',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'R7V_T8mZRGfrGkc6OSRv',
                  defaultMessage: 'Earn 2 Société Points per $1 spent on eligible purchases up to $15,000 per statement period.¹',
                  description: 'Body copy for the first highlight card on the Société Credit Card page',
                })}
              </BodyText>
              <FeatureCardTextButton kind="tertiary" size="medium" to={`/${regionCode.toLowerCase()}/${LOYALTY_SPEND_POINTS_PAGE}`} target="_blank">
                {intl.formatMessage({
                  id: 'Px8CpC0uikmDYiC0WA2c',
                  defaultMessage: 'Learn how points work',
                  description: 'Link text for the first highlight card on the Société Credit Card page',
                })}
              </FeatureCardTextButton>
            </FeatureCard>

            {!isStaff && (
              <FeatureCard>
                <FeatureCardImage>
                  <ResponsiveImage
                    id="societe-tile-150-credit"
                    fit="center"
                    objectFit="cover"
                  />
                </FeatureCardImage>
                <MobileFeatureCardContent>
                  <Heading variant="heading5">
                    {intl.formatMessage({
                      id: 'Bq2Vbjz9KjpF6ArWeh0M',
                      defaultMessage: '$150 Luxury Escapes credit',
                      description: 'Mobile heading for the second highlight card on the Société Credit Card page',
                    })}
                  </Heading>
                  <MobileBonusText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'iIo0X7FQNGAHHHvH51sH',
                      defaultMessage: 'Sign up bonus. Limited time only!',
                      description: 'Mobile bonus message for the second highlight card on the Société Credit Card page',
                    })}
                  </MobileBonusText>
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'kCXORYPvWwgCqTa4Xkkx',
                      defaultMessage: 'Spend $5,000 in your first 90 days and get $150 to use on your next booking.²',
                      description: 'Body copy for the second highlight card on the Société Credit Card page',
                    })}
                  </BodyText>
                </MobileFeatureCardContent>
                <TabletFeatureCardContent>
                  <Heading variant="heading5">
                    {intl.formatMessage({
                      id: 'I-WaczKlJjltU6QF9q3G',
                      defaultMessage: '$150 Luxury Escapes credit to use towards your next escape.',
                      description: 'Highlight card 2 heading on Société Credit Card page',
                    })}
                  </Heading>
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'kCXORYPvWwgCqTa4Xkkx',
                      defaultMessage: 'Spend $5,000 in your first 90 days and get $150 to use on your next booking.²',
                      description: 'Body copy for the second highlight card on the Société Credit Card page',
                    })}
                  </BodyText>
                </TabletFeatureCardContent>
              </FeatureCard>
            )}

            <FeatureCard>
              <FeatureCardImage>
                <ResponsiveImage
                  id="societe-creditcard-points-3"
                  fit="center"
                  objectFit="cover"
                />
              </FeatureCardImage>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: 'La9y4YQC6yuSzz_dyJkL',
                  defaultMessage: "Travel perks you'll love",
                  description: 'Highlight card 3 heading on Société Credit Card page',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'InLSEJWIe_xOYZrwAXnW',
                  defaultMessage: 'Complimentary lounge access when your flight is delayed, zero fees when you spend overseas and more.',
                  description: 'Body copy for the third highlight card on the Société Credit Card page',
                })}
              </BodyText>
            </FeatureCard>

            <FeatureCard>
              <FeatureCardImage>
                <ResponsiveImage
                  id="societe-tile-ready-in-minutes"
                  fit="center"
                  objectFit="cover"
                />
              </FeatureCardImage>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: 'yf2ksnt0MmMQA_-HtEix',
                  defaultMessage: 'Ready in minutes. Lives on your phone.',
                  description: 'Highlight card 4 heading on Société Credit Card page',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: '0UndyDoO-voApGzbW6lR',
                  defaultMessage: 'Apply digitally and start using your card immediately after approval — earning Société Points from your very first tap.',
                  description: 'Body copy for the fourth highlight card on the Société Credit Card page',
                })}
              </BodyText>
            </FeatureCard>
          </CardGrid>
          <HighlightCtaGroup direction="horizontal" horizontalAlign="center">
            <HighlightApplyButton fit="mobile-full-width" placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.highlights} />
          </HighlightCtaGroup>
        </VerticalSpacer>
      </LayoutContainer>
    </LoyaltyCreditCardSection>
  )
}

export default LoyaltyCreditCardHighlightsSection
